// Les entreprises à l'écran : leur logo, et la grille où l'on choisit les
// sites à suivre.
//
// La grille sert aux DEUX endroits où l'on choisit : l'étape « Vos sites » de
// la prise en main et le catalogue de « Sites suivis ». Deux listes de cases à
// cocher différentes, c'était deux dessins à tenir, et un nom d'entreprise en
// gras suivi de « recruitee · 12 offres » ne disait rien à un particulier : le
// nom de la plateforme est un détail d'exploitant.

import { useState } from 'react'
import { Building2, Check, Search } from 'lucide-react'
import type { OfferRow, SourceRow } from '../lib/api'
import { initials, jourCourt, parseDate, teinte } from '../lib/format'
import { useT } from '../lib/i18n'

/**
 * Le logo d'une entreprise, ou son monogramme coloré quand on n'en a pas ou
 * que l'image ne se charge pas. Un logo absent n'est donc jamais un trou :
 * c'est l'écran d'avant.
 */
export function LogoEntreprise({
  nom,
  logo,
  className = 'pc-logo',
}: {
  nom: string | null | undefined
  logo?: string | null
  className?: string
}) {
  const [echec, setEchec] = useState(false)
  if (logo && !echec) {
    return (
      <span className={`${className} a-logo`} aria-hidden="true">
        <img src={logo} alt="" loading="lazy" decoding="async" onError={() => setEchec(true)} />
      </span>
    )
  }
  // Employeur inconnu : un monogramme NEUTRE, pas deux lettres du nom du site
  // (« J( » pour jobs.lu, « EC » pour l'extension, issue #77).
  if (!nom) {
    return (
      <span className={`${className} logo-neutre`} aria-hidden="true">
        <Building2 size={14} />
      </span>
    )
  }
  return (
    <span className={className} style={teinte(nom)} aria-hidden="true">
      {initials(nom)}
    </span>
  )
}

/**
 * L'employeur d'une offre, ou, s'il est inconnu, d'où elle vient, en retrait.
 * Le nom du site ne se fait jamais passer pour l'employeur (issue #77) : sur
 * un agrégateur, « jobs.lu » n'a embauché personne.
 */
export function Employeur({ nom, source }: { nom: string | null | undefined; source?: string | null }) {
  const t = useT()
  if (nom) return <>{nom}</>
  return (
    <span className="employeur-inconnu">
      {source ? t('offre.employerViaSource', { source }) : t('offre.employerUnknown')}
    </span>
  )
}

/** « 9 oct. » pour la dernière lecture d'un site, « — » s'il n'a jamais été lu. */
export function jourDeLecture(s: string | null | undefined): string {
  const d = parseDate(s)
  return d ? jourCourt(d) : '—'
}

/**
 * Ce qu'on dit d'une offre republiée ou ancienne (issue #161), en une ligne
 * courte : une observation, jamais un verdict. Rien si elle n'a rien à dire.
 */
export function mentionSignaux(s: OfferRow['signaux'], t: ReturnType<typeof useT>): string | null {
  if (!s) return null
  const parts: string[] = []
  if (s.republications > 1) parts.push(t('offre.republishedMany', { n: s.republications + 1 }))
  else if (s.republications === 1) parts.push(t('offre.republished', { date: jourDeLecture(s.retireeLe) }))
  if (s.ageJours >= 60) parts.push(t('offre.oldPosting', { n: s.ageJours }))
  return parts.length ? parts.join(' · ') : null
}

/**
 * Ce qu'on dit d'un site que personne ne suit (issue #131) : la nuit ne le
 * relit pas, ses offres ont l'âge de sa dernière lecture. Rien pour un site
 * suivi, ni en console (`a_relire` y vaut toujours 0).
 */
export function mentionSansSuiveur(s: SourceRow, t: ReturnType<typeof useT>): string | null {
  if (!s.a_relire || s.abonnes > 0) return null
  return s.last_checked_at ? t('sources.notReadSince', { date: jourDeLecture(s.last_checked_at) }) : t('sources.neverRead')
}

/** Au-delà, une recherche aide ; en dessous, elle encombre. */
const SEUIL_RECHERCHE = 8

/**
 * Une grille de tuiles à cocher, une par site. `choisi` dit l'état d'une tuile,
 * `onBasculer` le change (une promesse : la tuile patiente pendant l'appel).
 */
export function GrilleSites({
  sites,
  choisi,
  onBasculer,
  disabled = false,
}: {
  sites: SourceRow[]
  choisi: (s: SourceRow) => boolean
  onBasculer: (s: SourceRow) => void | Promise<void>
  disabled?: boolean
}) {
  const t = useT()
  const [q, setQ] = useState('')
  const [enCours, setEnCours] = useState<number | null>(null)
  const terme = q.trim().toLowerCase()
  const trouves = sites
    .filter((s) => !terme || `${s.company ?? ''} ${s.name} ${s.url}`.toLowerCase().includes(terme))
    .sort((a, b) => b.offers - a.offers)

  return (
    <div className="grillesites">
      {sites.length > SEUIL_RECHERCHE && (
        <label className="gs-recherche">
          <Search size={15} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('sources.browseSearch')}
            aria-label={t('sources.browseSearch')}
          />
        </label>
      )}
      <div className="gs-tuiles">
        {trouves.map((s) => {
          const nom = s.company ?? s.name
          const coche = choisi(s)
          return (
            <button
              key={s.id}
              type="button"
              role="checkbox"
              aria-checked={coche}
              className={`gs-tuile${coche ? ' on' : ''}`}
              disabled={disabled || enCours === s.id}
              onClick={async () => {
                setEnCours(s.id)
                try {
                  await onBasculer(s)
                } finally {
                  setEnCours(null)
                }
              }}
            >
              <LogoEntreprise nom={nom} logo={s.logo} className="gs-logo" />
              <span className="gs-nom">{nom}</span>
              <span className="gs-n">{t('accueil.sites.offers', { n: s.offers })}</span>
              {mentionSansSuiveur(s, t) && <span className="gs-vieux">{mentionSansSuiveur(s, t)}</span>}
              <span className="gs-coche" aria-hidden="true">
                {enCours === s.id ? <span className="spinner" /> : <Check size={12} strokeWidth={3} />}
              </span>
            </button>
          )
        })}
      </div>
      {trouves.length === 0 && <p className="field-help">{t('sources.browseNone')}</p>}
    </div>
  )
}
